import {useEffect, useState} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import {styled} from 'styled-components';
import {
    Badge,
    Block,
    CustomForm,
    CustomFormTemplate,
    ErrorView,
    getStyledThemeColor,
    Link,
    MainLayout,
    Paper,
    Preloader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@flicent/fieldpulse-react-component-library-frontend';
import {getCustomFormsListRequest, getCustomFormTemplatesListRequest} from '@/services';


const StyledContent = styled.div`
    z-index: 1;
`;

const StyledRippleBackground = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${getStyledThemeColor('blue')};
    opacity: 0.5;
    pointer-events: none;

    .circle {
        position: absolute;
        border-radius: 50%;
        background: ${getStyledThemeColor('yellow')};
        animation: ripple 12s infinite;
        box-shadow: 0px 0px 1px 0px #508fb9;
    }

    .small {
        width: 200px;
        height: 200px;
        left: -100px;
        bottom: -100px;
    }

    .medium {
        width: 400px;
        height: 400px;
        left: -200px;
        bottom: -200px;
    }

    .large {
        width: 600px;
        height: 600px;
        left: -300px;
        bottom: -300px;
    }

    .xlarge {
        width: 800px;
        height: 800px;
        left: -400px;
        bottom: -400px;
    }

    .xxlarge {
        width: 1000px;
        height: 1000px;
        left: -500px;
        bottom: -500px;
    }

    .shade1 {
        opacity: 0.2;
    }

    .shade2 {
        opacity: 0.5;
    }

    .shade3 {
        opacity: 0.7;
    }

    .shade4 {
        opacity: 0.8;
    }

    .shade5 {
        opacity: 0.9;
    }

    @keyframes ripple {
        0% {
            transform: scale(0.8);
        }

        50% {
            transform: scale(1.2);
        }

        100% {
            transform: scale(0.8);
        }
    }
`;

export const AllFormsPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const location = useLocation();

    const [formsLoading, setFormsLoading] = useState<boolean>(false);
    const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);
    const [templateError, setTemplateError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [forms, setForms] = useState<CustomForm[]>([]);
    const [templates, setTemplates] = useState<CustomFormTemplate[]>([]);
    const [formsPage, setFormsPage] = useState(1);
    const [templatesPage, setTemplatesPage] = useState();

    // Fetch Template
    useEffect(() => {
        if (!token) {
            return;
        }

        setFormsLoading(true);
        setFormError(null);
        getCustomFormsListRequest({token, page: 1, limit: 10}).then(({response}) => {
            setForms(response);
        }).catch((error) => {
            setFormError(error.message);
        }).finally(() => {
            setFormsLoading(false);
        });


        setTemplatesLoading(true);
        setTemplateError(null);
        getCustomFormTemplatesListRequest({token, page: 1, limit: 40}).then(({response}) => {
            setTemplates(response);
        }).catch((error) => {
            setTemplateError(error.message);
        }).finally(() => {
            setTemplatesLoading(false);
        });
    }, []);

    if (!token) {
        return (
            <div>
                Token is required
            </div>
        )
    }

    return (
      <MainLayout>
          <StyledRippleBackground>
              <div style={ { position: 'absolute', bottom: 0, right: 20 } }>
                  <Typography variant="h1">
                      FieldPulse
                  </Typography>
              </div>
              <div className="ripple-background"/>
              <div className="circle xxlarge shade1"/>
              <div className="circle xlarge shade2"/>
              <div className="circle large shade3"/>
              <div className="circle mediun shade4"/>
              <div className="circle small shade5"/>
          </StyledRippleBackground>
          <StyledContent>
              <Block horizontalPaddingLess={ true }>
                  <div style={ { display: 'grid', gridTemplateColumns: '1fr 1fr', gridColumnGap: '30px' } }>
                      <Paper>
                          <Typography variant="h2" color="title">
                              All Templates
                              <div style={ { float: 'right' } }>
                                  <Link
                                    href={ '/custom-form-template' + location.search + location.hash }
                                    target="_blank"
                                  ><i className="fas fa-plus-circle"/>&nbsp;Template</Link>
                              </div>
                          </Typography>
                          <Table variant="bordered" gridTemplateColumns="50px 1fr 70px">
                              <TableHead>
                                  <TableRow>
                                      <TableCell>#</TableCell>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Actions</TableCell>
                                  </TableRow>
                              </TableHead>
                              <div style={ { overflowX: 'hidden', overflowY: 'auto', height: 500 } }>

                                  <TableBody>
                                      {
                                          templatesLoading ? <Preloader/> : templateError ?
                                            <ErrorView
                                              errorDetails={ templateError }/> : templates.map((template, index) => {
                                                const route = '/custom-form-template'
                                                  + '/' + template.id!.toString()
                                                  + '' + location.search
                                                  + '' + location.hash;

                                                return (
                                                  <TableRow key={ template.id }>
                                                      <TableCell>{ template.cuid }</TableCell>
                                                      <TableCell>
                                                          <Link href={ route } target="_blank">
                                                              { template.name }
                                                          </Link>
                                                      </TableCell>
                                                      <TableCell>
                                                          <div style={ { float: 'right' } }>
                                                              <Link
                                                                href={ `/custom-form?token=${ token }&template_id=${ template.id }${ location.hash }` }
                                                                target="_blank"
                                                              ><i className="fas fa-plus-circle"/>&nbsp;Form</Link>
                                                          </div>
                                                      </TableCell>
                                                  </TableRow>
                                                );
                                            })
                                      }
                                  </TableBody>
                              </div>
                          </Table>
                      </Paper>

                      <Paper>
                          <Typography variant="h2" color="title">All Forms</Typography>
                          <Table variant="bordered" gridTemplateColumns="auto 1fr">
                              <TableHead>
                                  <TableRow>
                                      <TableCell>#</TableCell>
                                      <TableCell>Name</TableCell>
                                  </TableRow>
                              </TableHead>
                              <div style={ { overflowX: 'hidden', overflowY: 'auto', height: 500 } }>

                                  <TableBody>
                                      {
                                          formsLoading ? <Preloader/> : formError ?
                                            <ErrorView errorDetails={ formError }/> : forms.map((form, index) => {
                                                const route = '/custom-form'
                                                  + '/' + form.id.toString()
                                                  + '' + location.search
                                                  + '' + location.hash;

                                                return (
                                                  <TableRow key={ form.id }>
                                                      <TableCell>{ form.cuid }.</TableCell>
                                                      <TableCell>
                                                          <Link href={ route } target="_blank">
                                                              { form.name }&nbsp;
                                                              { form.is_template ?
                                                                <Badge bgConstantColor="danger">Saved as
                                                                    Fill Template</Badge> : null }
                                                          </Link>
                                                      </TableCell>
                                                  </TableRow>
                                                );
                                            })
                                      }
                                  </TableBody>
                              </div>
                          </Table>
                      </Paper>
                  </div>
              </Block>
          </StyledContent>
      </MainLayout>
    );
};
